import {
  type JSX,
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
} from 'react';
import {clsx} from 'clsx';
import {paths} from '../lib/paths';
import {isContainerQueryAvailable} from '../lib/container-query-with-fallback';
import styles from './table.module.css';
import {PressReleaseInformationFileDownload} from './file-download';
import {PressReleaseInformationDownloadButton as DownloadButton} from './download-button';

type BusinessCategory = {
  busiCateId: string;
  busiCateName: string;
  busiCateLv2Id: number | null;
  busiCateLv2Name: string | null;
};

type Location = {
  prefId: string;
  prefName: string;
  cityId: string | null;
  cityName: string | null;
  categoryId: number;
  categoryName: string;
};

type FileDownloadProps = ComponentPropsWithoutRef<
  typeof PressReleaseInformationFileDownload
>;

type Props = {
  readonly releaseTypeName: string;
  readonly releaseTypeId: string;
  readonly businessCategories: BusinessCategory[];
  readonly locations: Location[];
  readonly keywords: string[];
  readonly referenceUrl: string | null;
  readonly renderDownload: () => JSX.Element;
  readonly renderPressKitLink: () => JSX.Element | null;
};

export function PressReleaseInformationTable({
  releaseTypeName,
  releaseTypeId,
  businessCategories,
  locations,
  keywords,
  referenceUrl,
  renderDownload,
  renderPressKitLink,
}: Props) {
  return (
    <dl
      className={clsx(
        styles.table,
        isContainerQueryAvailable()
          ? styles.tablePressReleaseRoot
          : styles.tableScreen,
      )}
    >
      <Row>
        <Term>種類</Term>
        <Definition>
          {releaseTypeName === '' || releaseTypeId === '' ? (
            <Item>未設定</Item>
          ) : (
            <Item>
              <a href={paths.releaseType(releaseTypeId)}>{releaseTypeName}</a>
            </Item>
          )}
        </Definition>
      </Row>

      <Row>
        <Term>ビジネスカテゴリ</Term>
        <Definition>
          {businessCategories.length > 0 ? (
            businessCategories.map((businessCategory) => (
              <Item
                key={
                  businessCategory.busiCateLv2Id ?? businessCategory.busiCateId
                }
              >
                <BusinessCategoryLink businessCategory={businessCategory} />
              </Item>
            ))
          ) : (
            <Item>未設定</Item>
          )}
        </Definition>
      </Row>

      {keywords.length > 0 && (
        <Row>
          <Term>キーワード</Term>
          <Definition>
            {keywords.map((keyword) => (
              <Item key={keyword}>
                <a href={paths.topics(keyword)}>{keyword}</a>
              </Item>
            ))}
          </Definition>
        </Row>
      )}

      {locations.length > 0 && (
        <Row>
          <Term>位置情報</Term>
          <Definition>
            {locations.map(
              ({
                prefId,
                prefName,
                cityId,
                cityName,
                categoryId,
                categoryName,
              }) => (
                <span
                  key={`${prefId}-${cityId}-${categoryId}`}
                  className={styles.location}
                >
                  <Anchor href={paths.location(prefId, cityId)}>
                    {prefName}
                    {cityName}
                  </Anchor>
                  <Anchor href={paths.locationCategory(categoryId)}>
                    （{categoryName}）
                  </Anchor>
                </span>
              ),
            )}
          </Definition>
        </Row>
      )}

      {referenceUrl !== null && (
        <Row>
          {/* 関連URL ?? 関連リンク ? */}
          <Term>関連リンク</Term>
          <Definition>
            <a
              href={referenceUrl}
              target='_blank'
              rel='nofollow noopener noreferrer'
              className={styles.referenceUrl}
            >
              {referenceUrl}
            </a>
          </Definition>
        </Row>
      )}

      {renderDownload()}

      <Row>
        <Term />
        <Definition>
          <div
            className={
              isContainerQueryAvailable()
                ? styles.pressKitLinkBoxPressReleaseRoot
                : styles.pressKitLinkBoxScreen
            }
          >
            {renderPressKitLink()}
          </div>
        </Definition>
      </Row>
    </dl>
  );
}

type PressReleaseInformationDownloadRowProps = {
  readonly isShow: {
    readonly downloadLink: boolean;
    readonly releaseDocs: boolean;
  };
  readonly companyId: number;
  readonly companyName: string;
  readonly releaseId: number;
  readonly pressReleaseFile: FileDownloadProps | null;
  readonly referenceFile: FileDownloadProps | null;
  readonly mediaUserOnlyPressReleaseFile: FileDownloadProps | null;
  readonly mediaUserOnlyReferenceFile: FileDownloadProps | null;
  readonly onClickDownload?: () => void;
};

export function PressReleaseInformationDownloadRow({
  isShow,
  companyId,
  releaseId,
  pressReleaseFile,
  referenceFile,
  mediaUserOnlyPressReleaseFile,
  mediaUserOnlyReferenceFile,
  onClickDownload,
}: PressReleaseInformationDownloadRowProps) {
  // ダウンロードボタン、資料ダウンロードのいずれも表示しない場合は null を返す
  if (!isShow.downloadLink && !isShow.releaseDocs) return null;

  const {downloadLink} = isShow;
  const files = [
    pressReleaseFile,
    referenceFile,
    mediaUserOnlyPressReleaseFile,
    mediaUserOnlyReferenceFile,
  ].flatMap((file) => (file === null ? [] : [file]));

  return (
    <Row>
      <Term>ダウンロード</Term>
      <Definition>
        <div
          className={clsx(
            styles.downloadButtonDef,
            isContainerQueryAvailable()
              ? styles.downloadButtonDefPressReleaseRoot
              : styles.downloadButtonDefScreen,
          )}
        >
          {files.length > 0 && (
            <div className={styles.documents}>
              {files.map((file) => (
                <PressReleaseInformationFileDownload
                  key={file.href}
                  {...file}
                />
              ))}
            </div>
          )}

          {downloadLink ? (
            <DownloadButton
              companyId={companyId}
              releaseId={releaseId}
              onClickDownload={onClickDownload}
            />
          ) : null}
        </div>
      </Definition>
    </Row>
  );
}

function Row({children}: PropsWithChildren) {
  return (
    <div
      className={clsx(
        styles.row,
        isContainerQueryAvailable()
          ? styles.rowPressReleaseRoot
          : styles.rowScreen,
      )}
    >
      {children}
    </div>
  );
}

function Term({children}: PropsWithChildren) {
  return <dt className={styles.term}>{children}</dt>;
}

function Definition({children}: PropsWithChildren) {
  return <dd className={styles.definition}>{children}</dd>;
}

function Item({children}: PropsWithChildren) {
  return <span className={styles.item}>{children}</span>;
}

function Anchor({
  href,
  rel,
  children,
}: PropsWithChildren<ComponentPropsWithoutRef<'a'>>) {
  return (
    <a className={styles.anchor} href={href} rel={rel}>
      {children}
    </a>
  );
}

function BusinessCategoryLink({
  businessCategory: {busiCateId, busiCateName, busiCateLv2Id, busiCateLv2Name},
}: {
  readonly businessCategory: BusinessCategory;
}) {
  /** busiCateLv2Id がない過去データについては省略する部分 */
  const lv2Segment = busiCateLv2Id === null ? '' : `/lv2/${busiCateLv2Id}/`;
  const href = `/main/html/searchbiscate/busi_cate_id/${busiCateId}${lv2Segment}`;

  /** busiCateLv2Name がない過去データについては busiCateName を表示 */
  const name = busiCateLv2Name ?? busiCateName;

  return <a href={href}>{name}</a>;
}
